import { useEffect, useRef } from 'react';

import { useVisualization } from '@/context/visualization-context';
import { cn } from '@/lib/utils';
import type { LayoutPreset } from '@/visualization/services/viewport-layout-service';

export interface ViewportContainerProps {
    /** Layout preset to apply */
    layout: LayoutPreset;
    /** Additional CSS classes */
    className?: string;
}

/**
 * ViewportContainer - Container for viewport layout rendering.
 *
 * Renders the container element that ViewportLayoutService uses to
 * create and arrange viewport grids. Handles:
 * - Container setup for layout service
 * - Resize observation and viewport updates
 * - Layout application and cleanup
 *
 * Note: Viewports automatically receive data through DisplayFeatureManager
 * events. No manual data population is required.
 */
export function ViewportContainer({ layout, className }: ViewportContainerProps) {
    const containerRef = useRef<HTMLDivElement>(null);
    const { getViewportLayoutService } = useVisualization();

    // Set up container and apply layout
    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        const layoutService = getViewportLayoutService();

        // Set container and apply layout
        // Viewports will automatically subscribe to DisplayFeatureManager events
        // and receive data when features are added
        layoutService.setContainer(container);
        layoutService.applyLayout(layout);

        // Set up resize observer
        const resizeObserver = new ResizeObserver(() => {
            layoutService.resizeAll();
        });
        resizeObserver.observe(container);

        // Cleanup on unmount or layout change
        return () => {
            resizeObserver.disconnect();
            layoutService.clearLayout();
        };
    }, [layout, getViewportLayoutService]);

    return <div ref={containerRef} className={cn('h-full w-full rounded-lg bg-background', className)} />;
}
